import PageHeader from '@/components/common/PageHeader';
import { useGetCurrentUser } from '@/hooks/app';
import { PermissionChecker } from '@/utils/permission';
import { Button, Modal } from 'antd';
import React, { useState } from 'react';
import { AddEditExchangeRateModal, CurrencyExchangeTable } from '../components';
import { DEFAULT_PAGE_SIZE, DEFAULT_STARTING_PAGE } from '../constants';
import {
  useCreateCurrency,
  useDeleteCurrency,
  useCurrencyList,
  useUpdateCurrency,
} from '../hooks';
import { ICurrency, ICurrencyForm } from '../types';
import { ICommonQueryParams } from '@/types';

export const CurrencyExchangePage: React.FC = () => {
  const { data: currentUser } = useGetCurrentUser();
  const permissions = new PermissionChecker(currentUser?.permission);

  const canManage = permissions.canManageCurrencyExchange();

  const [queryParams] = useState<ICommonQueryParams>({
    current: DEFAULT_STARTING_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    filter: '',
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<ICurrency | null>(null);

  const { data: listResponse, isLoading } = useCurrencyList({
    queryParams,
    enabled: true,
  });

  const createMutation = useCreateCurrency();
  const updateMutation = useUpdateCurrency();
  const deleteMutation = useDeleteCurrency();

  const currencies = listResponse?.value ?? [];

  const handleOpenAdd = () => {
    setEditingRecord(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (record: ICurrency) => {
    setEditingRecord(record);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRecord(null);
  };

  const handleSave = (values: ICurrencyForm) => {
    if (editingRecord) {
      updateMutation.mutate(
        { id: editingRecord.dogma_currencyid, payload: values },
        { onSuccess: handleCloseModal }
      );
    } else {
      createMutation.mutate(values, { onSuccess: handleCloseModal });
    }
  };

  const handleDelete = (record: ICurrency) => {
    Modal.confirm({
      title: 'Delete Currency',
      content: `Are you sure you want to delete ${record.dogma_name} (${record.dogma_isocurrencycode})?`,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: () => deleteMutation.mutate(record.dogma_currencyid),
    });
  };

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="p-4 space-y-4">
      <PageHeader
        title="Currency Exchange"
        subtitle="Manage currency exchange rates used across the platform. Rates are defined as 1 GBP = X Foreign Currency (e.g. 1 GBP = 1.18 EUR)."
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Currency Exchange' },
        ]}
        actions={
          canManage ? (
            <Button type="primary" onClick={handleOpenAdd}>
              Add Currency
            </Button>
          ) : undefined
        }
      />

      <div className="space-y-4 p-4">
        <CurrencyExchangeTable
          isLoading={isLoading}
          currencies={currencies}
          canManage={canManage}
          onEdit={handleOpenEdit}
          onDelete={handleDelete}
        />
      </div>

      <AddEditExchangeRateModal
        open={modalOpen}
        editingRecord={editingRecord}
        isLoading={isSaving}
        onCancel={handleCloseModal}
        onSave={handleSave}
      />
    </div>
  );
};
